import { useTranslation } from "react-i18next";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  alphabetizeInstitutions,
  useAllInstitutions
} from "../Institutions/institutionUtils";
import { Alert, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { ProgramReport } from "./reportUtils";
import { GridColDef } from "@mui/x-data-grid";
import { useAppSelector } from "../../app/hooks";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import { BackendProgramReport } from "./BackendProgramReport";
import { FrontendProgramReport } from "./FrontendProgramReport";
import { DynamicProgramReport } from "./DynamicProgramReport";
import { ReportsDateRangePicker } from "./ReportsDateRangePicker";
import { sub } from "date-fns";
import { ReportsPageProps } from "../../app/types";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";

const pageDetails: PageWrapperProps = {
  className: "ProgramReports",
  headerText: "Program Reports"
};

export const ProgramReports = ({ classicMode }: ReportsPageProps) => {
  const { t } = useTranslation();
  const currentUserPermissions = useAppSelector(selectUserCognitoGroups);
  const isProgramAdmin = currentUserPermissions?.includes(
    CognitoUserGroups.ProgramAdmins
  );
  const {
    status: fetchInstitutionsStatus,
    error: fetchInstitutionsError,
    institutions
  } = useAllInstitutions();

  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { use_dynamic_reports, use_backend_reports } = flags;

  const sortedInstitutions = institutions
    ? alphabetizeInstitutions(institutions)
    : institutions;

  const [institutionId, setInstitutionId] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<Date | null>(
    sub(new Date(), { months: 1 })
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setInstitutionId(event.target.value);
  };

  const columns: GridColDef<ProgramReport>[] = [
    {
      field: "institutionName",
      headerName: t("Institution"),
      valueGetter: (params) => params.row.institutionName,
      flex: 2
    },
    {
      field: "programName",
      headerName: t("Program"),
      flex: 2
    },
    {
      field: "createdAssessmentEvents",
      headerName: t("# Created"),
      flex: 1
    },
    {
      field: "assignedAssessmentEvents",
      headerName: t("# Assigned"),
      flex: 1
    },
    {
      field: "completedAssessmentEvents",
      headerName: t("# Completed"),
      flex: 1
    },
    {
      field: "percentCompleted",
      headerName: t("% Completed"),
      flex: 1
    },
    {
      field: "attendingsInProgram",
      headerName: t("# of Attendings"),
      flex: 1
    },
    {
      field: "traineesInProgram",
      headerName: t("# of Trainees"),
      flex: 1
    },
    {
      field: "averageCompletedPerTrainee",
      headerName: t("Average # Completed per Trainee"),
      flex: 1
    }
  ];

  const useDynamicReports = !classicMode && use_dynamic_reports;
  const useBackendReports =
    classicMode || (use_backend_reports && !use_dynamic_reports);

  const pageBody = (() => {
    if (useDynamicReports) {
      return (
        <>
          <ReportsDateRangePicker
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />
          <DynamicProgramReport
            institutionId={institutionId}
            columns={columns}
            startDate={fromDate?.toISOString()?.substring(0, 10)}
            endDate={toDate?.toISOString()?.substring(0, 10)}
          />
        </>
      );
    } else if (useBackendReports) {
      return (
        <BackendProgramReport institutionId={institutionId} columns={columns} />
      );
    }
    return (
      <FrontendProgramReport institutionId={institutionId} columns={columns} />
    );
  })();

  if (isProgramAdmin) {
    return (
      <PageWrapper {...pageDetails}>
        <Typography variant="h2">{t("Not allowed!")}</Typography>
        <Typography variant="h3">
          {t("Program admins can only generate user-level reports.")}
        </Typography>
      </PageWrapper>
    );
  }

  if (fetchInstitutionsStatus === "idle") {
    return (
      <PageWrapper {...pageDetails}>
        <Typography variant="h2">{t("Awaiting data...")}</Typography>
      </PageWrapper>
    );
  }

  if (fetchInstitutionsStatus === "loading") {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (fetchInstitutionsStatus === "failed") {
    return (
      <PageWrapper {...pageDetails}>
        {fetchInstitutionsError && (
          <Alert severity="error">{fetchInstitutionsError}</Alert>
        )}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...pageDetails}>
      <div>
        <select onChange={handleChange}>
          <option value={undefined}>{t("(Select Institution)")}</option>
          {sortedInstitutions?.map((institution) => (
            <option value={institution.id}>{institution.name}</option>
          ))}
        </select>
      </div>
      {pageBody}
    </PageWrapper>
  );
};
